/* eslint-disable indent,quotes */
import {observer} from 'mobx-react-lite';
import * as React from 'react';
import {FC} from 'react';

import {isTestEnv} from '../helpers/helpers';
import {LogType} from '../swagger';
import {Card} from './Card';

export interface IntegrationInstructionsProps {
    logType?: LogType;
    title?: string | null;
    logId?: string | null;
}

const harvesterUrl = isTestEnv() ? 'harvesterUrl: \'https://harvester.test.muscula.com\',' : '';
const jsFile = isTestEnv() ? 'https://cdn.muscula.com/m2v2.js' : 'https://cdn.muscula.com/m2v2.js';

export const IntegrationInstructions: FC<IntegrationInstructionsProps> = observer(({logType, title, logId}) => (
    <Card title={'Integration instructions for log ' + title + ' (ID: ' + logId + ')'}>
        {logType === LogType.GenericJs && (
            <React.Fragment>
                <p className="mb-2">Add this code on top of html file, just after &lt;head&gt; part:</p>
                <pre className="code-fragment">
                    {`<script type="text/javascript">
    window.Muscula = {
            logId: '${logId}',
            errors: [],${harvesterUrl}
        };
        window.Muscula.onerrorOriginalHandler = window.onerror;
        window.onerror = function () {
            window.Muscula.errors.push(arguments);
            return false;
        };

        (function () {
            var m = document.createElement('script');
            m.async = true;
            m.src = '${jsFile}';
            var s = document.getElementsByTagName('script')[0];
            s.parentNode.insertBefore(m, s);
        })();
</script>
    `}
                </pre>
            </React.Fragment>
        )}

        {logType === LogType.AppJs && (
            <div>
                1. Install npm package{' '}
                <a
                    href="https://www.npmjs.com/package/@muscula.com/muscula-webapp-js-logger"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    muscula-webapp-js-logger
                </a>
                <pre className="code-fragment">
                    npm install --save @muscula.com/muscula-webapp-js-logger
                </pre>
                <br/>
                2. At application startup add:
                <pre className="code-fragment">
                    {`
import MusculaLog from '@muscula.com/muscula-webapp-js-logger';
MusculaLog.Init('${logId}')`}
                </pre>
                <br/>
                2. Use it where you need, but all errors will be catched automatically.
                <pre className="code-fragment">
                    {`try {
    throw new Error('Error');
} catch(exception) {
    MusculaLog.Error('Something wrong', exception, {userId: ''}})
}`}
                </pre>
            </div>
        )}

        {logType === LogType.DotnetCoreMicrosoftExtensions && (
            <div>
              1. Install nuget package{' '}
              <a
                href="https://www.nuget.org/packages/Muscula.Csharp.Extensions.Logging.AspNetCore/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Muscula.Csharp.Extensions.Logging.AspNetCore
              </a>
              <br />
              2. In Program.cs add code like:
              <pre className="code-fragment">
                    {`if (!builder.Environment.IsDevelopment())
{
    builder.Logging.AddMuscula((settingsBuilder) =>
    {
        settingsBuilder.UseLogId("${logId}");
        return settingsBuilder.Build();
    });
}`}
                </pre>
            </div>
        )}

        {logType === LogType.DotnetLog4Net && (
          <div>
            1. Install nuget package{' '}
            <a
              href="https://www.nuget.org/packages/Muscula.Csharp.Extensions.Logging.Log4Net/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Muscula.Csharp.Extensions.Logging.Log4Net
            </a>
            <br />
            2. In Program.cs add
            <pre className="code-fragment">{`builder.Services.AddLogging(b => b.AddLog4Net("log4net.config"));`}</pre>
            <br />
            3. Provide log4net.config file
            <pre className="code-fragment">
                    {`<?xml version="1.0" encoding="utf-8" ?>
<log4net>
    <appender name="MusculaAppender" type="Muscula.Csharp.Extensions.Logging.Log4Net.MusculaAppender, Muscula.Csharp.Extensions.Logging.Log4Net" >
        <layout type="log4net.Layout.PatternLayout">
            <conversionPattern value="%date [%thread] %-5level %logger [%ndc] - %message%newline" />
        </layout>
        <MusculaSettings>
            <LogId value="${logId}" />
        </MusculaSettings>
    </appender>
    <root>
        <level value="ALL" />
        <appender-ref ref="MusculaAppender" />
    </root>
</log4net>`}
                </pre>
            </div>
        )}


        {logType === LogType.JavaLog4J && (
            <div>
                1. Add dependency to pom file
                <pre className="code-fragment">
                    {`
<dependency>
    <groupId>com.muscula</groupId>
    <artifactId>muscula-log4j</artifactId>
</dependency>
                    `}
                </pre>
                <br/>
                2. Configure log4j in file src/main/resources/log4j2.xml
                <pre className="code-fragment">
                    {`<?xml version="1.0" encoding="UTF-8"?>
<Configuration status="INFO">
    <Appenders>
        <MusculaAppender name="MusculaAppender" logId="${logId}"/>
    </Appenders>
    <Loggers>
        <Root level="error">
            <AppenderRef ref="MusculaAppender"/>
        </Root>
    </Loggers>
</Configuration>
                    `}
                </pre>
            </div>
        )}

        {logType === LogType.JavaLogback && (
            <div>
                1. Add dependency to pom file
                <pre className="code-fragment">
                    {`
<dependency>
    <groupId>com.muscula</groupId>
    <artifactId>muscula-logback</artifactId>
</dependency>
                    `}
                </pre>
                <br/>
                3. Configure logback in file src/main/resources/logback.xml
                <pre className="code-fragment">
                    {`<configuration>
    <appender name="muscula" class="com.muscula.logback.appender.MusculaAppender">
        <logId>${logId}</logId>
    </appender>
    <root level="info">
        <appender-ref ref="muscula"/>
    </root>
</configuration>

                    `}
                </pre>
                <br/>
                4. Use it like usual with logback
                <pre className="code-fragment">

                    {`public class Main {
    private static final Logger logger = LoggerFactory.getLogger(Main.class);
    public static void main(String[] args) {
        for(int i=0; i<10; i++)
        logger.error("test", new RuntimeException("logback error test"));
    }
}`}
                </pre>
            </div>
        )}

        {logType === LogType.Php && (
            <div>
                1. Install with composer
                <pre className="code-fragment">
                    {`{
    "require": {
        "muscula/muscula-php-logger": "^1.0"
    }
}`}
                </pre><br/>
                or
                <br/>
                <br/>
                <pre className="code-fragment">
                {`composer require muscula/muscula-php-logger`}
                </pre>
                <br/>
                <br/>

                2. Handle errors:
                There is a Muscula\Handler class that initializes PHP errors & exceptions handlers. Insert this code at begining of your application (ie.
                index.php).

                <pre className="code-fragment">
                    {`$handler = Muscula\\Handler::getInstance();
$handler->start('${logId}');`}
                </pre>
                <br/>

                3. More information
                At <a href="https://packagist.org/packages/muscula/muscula-php-logger"
                      target="_blank" rel="noopener noreferrer">https://packagist.org/packages/muscula/muscula-php-logger</a>
                you can find full documentation
            </div>

        )}

        {logType === LogType.WordPress && (
            <div>
                1. Install WordPress plugin
                <pre className="code-fragment">
                    {`https://wordpress.org/plugins/muscula/`}
                </pre><br/>

                2. Enter in plugin configuration Log ID: <br/><span className="font-bold">{`${logId}`}</span><br/>
            </div>
        )}

        <br/>
        <br/>
        <h3>If you have any questions, or something doesn't work for you, please contact us at <a
            href="mailto:support@muscula.com">support@muscula.com</a></h3>
    </Card>
));
